import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Search, MapPin, Users, Home } from "lucide-react";

export default function Panchayats() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/panchayats", { params: q ? { search: q } : {} }).then((r) => setList(r.data));
  }, [q]);

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("panchayats")}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">{t("panchayats")}</h1>
            <p className="mt-1 text-sm text-slate-600">{lang === "hi" ? "हरियाणा की सभी ग्राम पंचायतों की सूची" : "All Gram Panchayats across Haryana."}</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="panchayat-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search") + "…"}
              className="pl-9 pr-3 py-2 rounded-md border border-slate-300 bg-white text-sm w-72 outline-none focus:border-[#0A3161]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="panchayat-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">{t("district")}</th>
                  <th className="text-left px-4 py-3">{t("block")}</th>
                  <th className="text-right px-4 py-3">{t("population")}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${selected?.id === p.id ? "bg-[#0A3161]/5" : ""}`}
                    onClick={() => setSelected(p)}
                    data-testid={`pan-row-${p.code}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.code}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      {p.name_hi && <div className="text-xs text-slate-500">{p.name_hi}</div>}
                    </td>
                    <td className="px-4 py-3">{p.district}</td>
                    <td className="px-4 py-3">{p.block}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.population?.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr><td colSpan={5} className="py-12 text-center text-slate-400 text-sm">No panchayats found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-5">
            {selected ? (
              <div data-testid="panchayat-detail">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{t("panchayat")}</div>
                <h3 className="mt-2 font-heading text-xl font-bold text-slate-900">{selected.name}</h3>
                {selected.name_hi && <div className="text-sm text-slate-600">{selected.name_hi}</div>}
                <div className="mt-1 font-mono text-xs text-slate-500">{selected.code}</div>

                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-start gap-2 text-slate-700">
                    <MapPin className="w-4 h-4 mt-0.5 text-slate-400" />
                    <span>{selected.block}, {selected.district}, {selected.state}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("population")}</div>
                      <div className="flex items-center gap-1 mt-0.5 font-heading font-bold text-slate-900"><Users className="w-3.5 h-3.5" />{selected.population?.toLocaleString("en-IN")}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("households")}</div>
                      <div className="flex items-center gap-1 mt-0.5 font-heading font-bold text-slate-900"><Home className="w-3.5 h-3.5" />{selected.households?.toLocaleString("en-IN")}</div>
                    </div>
                    <div className="col-span-2 pt-2 border-t border-slate-100">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("sarpanch")}</div>
                      <div className="font-semibold text-slate-900">{selected.sarpanch_name || "—"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("secretary")}</div>
                      <div className="font-semibold text-slate-900">{selected.secretary_name || "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-10 text-center">Select a panchayat to view details</div>
            )}
          </div>
        </div>
      </div>
    </OfficerLayout>
  );
}
